import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import "./PortalCliente.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";

const normalizeAddress = (value) =>
  String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("it-IT");
};

const formatEuro = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);
};

const truncate = (value, max = 180) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const categoryIcon = (categoria) => {
  switch (categoria) {
    case "foto_impianto": return "▧";
    case "documenti_firmati": return "✓";
    case "verbale_collaudo": return "◎";
    case "manualistica": return "≡";
    case "certificazioni": return "★";
    default: return "•";
  }
};

const questoSitoLabel = (nome) => `questo sito (${nome})`;

const PortalCliente = () => {
  const [token, setToken] = useState(() => localStorage.getItem("portalToken") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [me, setMe] = useState(null);
  const [preventivi, setPreventivi] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [interventi, setInterventi] = useState([]);
  const [siti, setSiti] = useState([]);
  const [sitoAttivoId, setSitoAttivoId] = useState("tutti");
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState("");
  const [activeSection, setActiveSection] = useState("panoramica");

  useEffect(() => {
    document.body.classList.add("betalumina-portal-active");
    return () => document.body.classList.remove("betalumina-portal-active");
  }, []);

  const portaPortaleInAlto = useCallback(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const cambiaSezione = useCallback(
    (sezione) => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      setActiveSection(sezione);
    },
    []
  );

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    portaPortaleInAlto();

    const frame = window.requestAnimationFrame(() => {
      portaPortaleInAlto();
      window.requestAnimationFrame(portaPortaleInAlto);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeSection, portaPortaleInAlto]);

  /*
   * Firefox can restore/anchor the document scroll after the async portal data
   * has been committed. The Siti / Impianti strip is conditional on siti.length,
   * so the first authenticated render changes the document height above the nav.
   * Reset only after hydration is complete, not just at component mount.
   */
  useLayoutEffect(() => {
    if (!token || loading || !me) return;

    const reset = () => portaPortaleInAlto();

    reset();
    const timers = [
      window.setTimeout(reset, 0),
      window.setTimeout(reset, 80),
      window.setTimeout(reset, 250),
      window.setTimeout(reset, 700),
      window.setTimeout(reset, 1500),
    ];

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [token, loading, me, siti.length, portaPortaleInAlto]);

  useEffect(() => {
    const onPageShow = () => {
      if (!loading && me) portaPortaleInAlto();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [loading, me, portaPortaleInAlto]);

  const resetPortalData = useCallback(() => {
    setMe(null);
    setPreventivi([]);
    setDocumenti([]);
    setInterventi([]);
    setSiti([]);
    setSitoAttivoId("tutti");
  }, []);

  const portalFetch = useCallback(async (path, options = {}) => {
    const headers = new Headers(options.headers || {});
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (options.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem("portalToken");
      setToken("");
      resetPortalData();
    }

    return response;
  }, [token, resetPortalData]);

  const caricaPortale = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrore("");

    try {
      const [meRes, prevRes, docRes, intRes, sitiRes] = await Promise.all([
        portalFetch("/api/portal/me"),
        portalFetch("/api/portal/preventivi"),
        portalFetch("/api/portal/documenti"),
        portalFetch("/api/portal/interventi"),
        portalFetch("/api/portal/siti"),
      ]);

      if (!meRes.ok || !prevRes.ok || !docRes.ok || !intRes.ok || !sitiRes.ok) {
        throw new Error("Impossibile caricare completamente il portale cliente.");
      }

      const [meData, prevData, docData, intData, sitiData] = await Promise.all([
        meRes.json(), prevRes.json(), docRes.json(), intRes.json(), sitiRes.json()
      ]);

      setMe(meData);
      setPreventivi(Array.isArray(prevData) ? prevData : []);
      setDocumenti(Array.isArray(docData) ? docData : []);
      setInterventi(Array.isArray(intData) ? intData : []);
      setSiti(Array.isArray(sitiData) ? sitiData : []);
    } catch (error) {
      setErrore(error.message || "Errore caricamento portale.");
    } finally {
      setLoading(false);
    }
  }, [token, portalFetch]);

  useEffect(() => { caricaPortale(); }, [caricaPortale]);

  const login = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrore("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/portal/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.token) {
        throw new Error(data.message || "Credenziali non valide.");
      }

      localStorage.setItem("portalToken", data.token);
      setToken(data.token);
      setPassword("");
    } catch (error) {
      setErrore(error.message || "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("portalToken");
    setToken("");
    resetPortalData();
    setEmail("");
    setPassword("");
    setErrore("");
    setActiveSection("panoramica");
  };

  const downloadBlob = async (path, fallbackName) => {
    const response = await portalFetch(path);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "File non disponibile.");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const cd = response.headers.get("content-disposition") || "";
    const match = cd.match(/filename="?([^";]+)"?/i);

    link.href = url;
    link.download = match?.[1] || fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPreventivo = async (preventivo) => {
    setErrore("");
    try {
      await downloadBlob(`/api/portal/preventivi/${preventivo.id}/pdf`, `Preventivo_${preventivo.id}.pdf`);
    } catch (error) {
      setErrore(error.message || "Errore download PDF.");
    }
  };

  const downloadDocumento = async (documento) => {
    setErrore("");
    try {
      await downloadBlob(documento.downloadUrl, documento.nome || "documento");
    } catch (error) {
      setErrore(error.message || "Errore download documento.");
    }
  };

  const sitoAttivo = useMemo(
    () => sitoAttivoId === "tutti"
      ? null
      : siti.find((s) => String(s.id) === String(sitoAttivoId)) || null,
    [siti, sitoAttivoId]
  );

  const preventiviVisibili = useMemo(() => {
    if (!sitoAttivo) return preventivi;
    const targetId = Number(sitoAttivo.id);
    const targetAddress = normalizeAddress(sitoAttivo.indirizzo);
    return preventivi.filter((p) => {
      const recordSiteId = Number(p?.sito_id);
      if (Number.isFinite(recordSiteId) && recordSiteId > 0) return recordSiteId === targetId;
      return normalizeAddress(p?.indirizzo) === targetAddress;
    });
  }, [preventivi, sitoAttivo]);

  const interventiVisibili = useMemo(() => {
    if (!sitoAttivo) return interventi;
    const targetId = Number(sitoAttivo.id);
    const targetAddress = normalizeAddress(sitoAttivo.indirizzo);
    return interventi.filter((i) => {
      const recordSiteId = Number(i?.sito_id);
      if (Number.isFinite(recordSiteId) && recordSiteId > 0) return recordSiteId === targetId;
      return normalizeAddress(i?.indirizzo) === targetAddress;
    });
  }, [interventi, sitoAttivo]);

  const preventiviAperti = useMemo(
    () => preventiviVisibili.filter((p) => {
      const stato = String(p.stato || "").toLowerCase();
      return !["completato", "rifiutato", "annullato"].includes(stato);
    }).length,
    [preventiviVisibili]
  );

  const documentiPerCategoria = useMemo(() => {
    const groups = {};
    documenti.forEach((doc) => {
      const key = doc.categoriaLabel || "Documenti";
      if (!groups[key]) groups[key] = [];
      groups[key].push(doc);
    });
    return groups;
  }, [documenti]);

  if (!token) {
    return (
      <div className="portal-page portal-login-page">
        <div className="portal-login-card">
          <div className="portal-brand">
            <div className="portal-brand-mark">B</div>
            <div>
              <div className="portal-brand-name">BetaLumina</div>
              <div className="portal-brand-subtitle">Portale Cliente</div>
            </div>
          </div>
          <h1>Il tuo impianto, sempre con te.</h1>
          <p className="portal-muted">
            Accedi alla tua area riservata per consultare siti, preventivi,
            documentazione tecnica e storico degli interventi.
          </p>
          <form onSubmit={login} className="portal-login-form">
            <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required /></label>
            <label>Password<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required /></label>
            {errore && <div className="portal-error">{errore}</div>}
            <button type="submit" className="portal-primary" disabled={loading}>
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </form>
          <div className="portal-login-note">Area riservata ai clienti BetaLumina.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <header className="portal-header">
        <div className="portal-brand">
          <div className="portal-brand-mark">B</div>
          <div>
            <div className="portal-brand-name">BetaLumina</div>
            <div className="portal-brand-subtitle">Portale Cliente</div>
          </div>
        </div>
        <div className="portal-header-actions">
          <button type="button" className="portal-refresh" onClick={caricaPortale} disabled={loading}>
            {loading ? "Aggiorno..." : "Aggiorna"}
          </button>
          <button type="button" className="portal-logout" onClick={logout}>Esci</button>
        </div>
      </header>

      <main className="portal-main">
        <section className="portal-hero portal-hero-unified">
          <div>
            <div className="portal-eyebrow">
              {activeSection === "panoramica"
                ? "AREA RISERVATA"
                : activeSection === "preventivi"
                ? "AREA COMMERCIALE"
                : activeSection === "documenti"
                ? "FASCICOLO DIGITALE"
                : "STORICO TECNICO"}
            </div>

            <h1>
              {activeSection === "panoramica"
                ? me?.nome
                  ? `Bentornato, ${me.nome}`
                  : "Bentornato nel Portale Cliente"
                : activeSection === "preventivi"
                ? "I tuoi preventivi"
                : activeSection === "documenti"
                ? "Documentazione tecnica"
                : "Interventi e manutenzioni"}
            </h1>

            <p>
              {activeSection === "panoramica"
                ? "Qui trovi in un unico spazio i tuoi siti, i preventivi, la documentazione tecnica e lo storico degli interventi BetaLumina."
                : activeSection === "preventivi"
                ? "Consulta le proposte economiche associate ai tuoi siti e scarica i PDF disponibili."
                : activeSection === "documenti"
                ? "Consulta manuali, verbali, certificazioni e documenti tecnici disponibili."
                : "Consulta lo storico degli interventi completati e delle manutenzioni effettuate."}
            </p>
          </div>

          <div className="portal-client-code">
            Cliente #{me?.clienteId || "—"}
          </div>
        </section>

        {errore && <div className="portal-error portal-error-wide">{errore}</div>}

        {siti.length > 0 && (
          <section className="portal-sites-strip">
            <div className="portal-sites-strip-head">
              <div className="portal-sites-title">
                <div className="portal-eyebrow">SITI / IMPIANTI</div>
                <strong>{sitoAttivo ? sitoAttivo.nome : "Seleziona il sito"}</strong>
              </div>
              {sitoAttivo && <span>{sitoAttivo.indirizzo}</span>}
            </div>

            <div className="portal-site-pills">
              <button type="button" className={sitoAttivoId === "tutti" ? "active" : ""} onClick={() => setSitoAttivoId("tutti")}>
                <strong>Tutti</strong><small>Vista generale</small>
              </button>
              {siti.map((sito) => (
                <button type="button" key={sito.id} className={String(sitoAttivoId) === String(sito.id) ? "active" : ""} onClick={() => setSitoAttivoId(String(sito.id))}>
                  <strong>{sito.nome}</strong>
                  <small>{Number(sito.preventivi || 0)} prev. · {Number(sito.interventi || 0)} int.</small>
                </button>
              ))}
            </div>
          </section>
        )}

        <nav className="portal-nav" aria-label="Sezioni portale cliente">
          {[
            ["panoramica", "Panoramica"],
            ["preventivi", `Preventivi ${preventiviVisibili.length}`],
            ["documenti", `Documenti ${documenti.length}`],
            ["interventi", `Interventi ${interventiVisibili.length}`],
          ].map(([key, label]) => (
            <button key={key} type="button" className={activeSection === key ? "active" : ""} onClick={() => cambiaSezione(key)}>
              {label}
            </button>
          ))}
        </nav>

        {(activeSection === "panoramica" || activeSection === "preventivi") && (
          <>
            <section className="portal-stats">
              <button type="button" className="portal-stat-card" onClick={() => cambiaSezione("preventivi")}>
                <span>Preventivi</span><strong>{preventiviVisibili.length}</strong><small>{preventiviAperti} in evidenza</small>
              </button>
              <button type="button" className="portal-stat-card" onClick={() => cambiaSezione("documenti")}>
                <span>Documentazione impianto</span><strong>{documenti.length}</strong><small>file disponibili</small>
              </button>
              <button type="button" className="portal-stat-card" onClick={() => cambiaSezione("interventi")}>
                <span>Interventi completati</span><strong>{interventiVisibili.length}</strong><small>storico tecnico</small>
              </button>
            </section>

            <section className="portal-section">
              {activeSection === "panoramica" && (
                <div className="portal-section-head">
                  <div>
                    <div className="portal-eyebrow">COMMERCIALE</div>
                    <h2>I miei preventivi</h2>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="portal-empty">Caricamento...</div>
              ) : preventiviVisibili.length === 0 ? (
                <div className="portal-empty">
                  Non ci sono ancora preventivi associati a {sitoAttivo ? questoSitoLabel(sitoAttivo.nome) : "questa anagrafica"}.
                </div>
              ) : (
                <div className="portal-grid">
                  {(activeSection === "panoramica" ? preventiviVisibili.slice(0, 3) : preventiviVisibili).map((p) => (
                    <article key={p.id} className="portal-card portal-quote-card">
                      <div className="portal-card-top">
                        <span className="portal-card-number">Preventivo #{p.id}</span>
                        <span className="portal-badge">{p.stato || "Emesso"}</span>
                      </div>
                      <h3>{p.oggetto || "Preventivo BetaLumina"}</h3>
                      <div className="portal-card-meta">
                        <span>Data: {formatDate(p.dataEmissione)}</span>
                        {p.validita ? <span>Validità: {p.validita} gg</span> : null}
                      </div>
                      {p.indirizzo ? <div className="portal-address">{p.indirizzo}</div> : null}
                      <div className="portal-card-footer">
                        <div><span className="portal-total-label">Totale</span><strong>{formatEuro(p.costoTotaleConIva)}</strong></div>
                        <button type="button" className="portal-primary portal-download" onClick={() => downloadPreventivo(p)}>Scarica PDF</button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {(activeSection === "panoramica" || activeSection === "documenti") && (
          <section className="portal-section">
            {activeSection === "panoramica" && (
              <div className="portal-section-head">
                <div>
                  <div className="portal-eyebrow">FASCICOLO DIGITALE IMPIANTO</div>
                  <h2>Documentazione tecnica</h2>
                </div>
              </div>
            )}

            {loading ? (
              <div className="portal-empty">Caricamento...</div>
            ) : documenti.length === 0 ? (
              <div className="portal-empty">Nessun documento tecnico è stato ancora condiviso.</div>
            ) : (
              <div className="portal-document-groups">
                {Object.entries(documentiPerCategoria).map(([categoria, docs]) => (
                  <div key={categoria} className="portal-document-group">
                    <div className="portal-document-group-title">{categoria}<span>{docs.length}</span></div>
                    <div className="portal-document-list">
                      {docs.map((doc) => (
                        <button type="button" key={doc.id} className="portal-document-row" onClick={() => downloadDocumento(doc)}>
                          <span className="portal-document-icon">{categoryIcon(doc.categoria)}</span>
                          <span className="portal-document-name">{doc.nome}</span>
                          <span className="portal-document-action">Scarica</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {(activeSection === "panoramica" || activeSection === "interventi") && (
          <section className="portal-section">
            {activeSection === "panoramica" && (
              <div className="portal-section-head">
                <div>
                  <div className="portal-eyebrow">STORICO TECNICO</div>
                  <h2>Interventi e manutenzioni</h2>
                </div>
              </div>
            )}

            {loading ? (
              <div className="portal-empty">Caricamento...</div>
            ) : interventiVisibili.length === 0 ? (
              <div className="portal-empty">
                Nessun intervento completato risulta ancora associato a {sitoAttivo ? questoSitoLabel(sitoAttivo.nome) : "questa anagrafica"}.
              </div>
            ) : (
              <div className="portal-timeline">
                {(activeSection === "panoramica" ? interventiVisibili.slice(0, 3) : interventiVisibili).map((item) => (
                  <article key={item.id} className="portal-intervention-card">
                    <div className="portal-timeline-dot" />
                    <div className="portal-intervention-body">
                      <div className="portal-card-top">
                        <span className="portal-card-number">{item.numero_foglio_intervento || `Intervento #${item.id}`}</span>
                        <span className="portal-badge portal-badge-blue">{item.stato || "Completato"}</span>
                      </div>
                      <h3>{item.causale || item.descrizione || "Intervento tecnico"}</h3>
                      <div className="portal-card-meta">
                        <span>Data: {formatDate(item.dataEsecuzione || item.data)}</span>
                        {item.tecnico ? <span>Tecnico: {item.tecnico}</span> : null}
                      </div>
                      {item.indirizzo ? <div className="portal-address">{item.indirizzo}</div> : null}
                      {item.lavoriEseguiti ? <p className="portal-work-summary">{truncate(item.lavoriEseguiti)}</p> : null}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        <footer className="portal-footer">
          <div>BetaLumina · Impianti tecnologici e sicurezza</div>
          <small>I contenuti del portale sono associati alla tua anagrafica cliente.</small>
        </footer>
      </main>
    </div>
  );
};

export default PortalCliente;
